import type { MiProfileDateMode } from "../stores/profileStore";
import {
  clampYmdRangeToBounds,
  dateRangeForPreset,
  isValidYmdRange,
  parseYmdRangeFromSearchParams,
  type MiContenidoDateFilterCompany,
  type MiContenidoDateFilterUser,
} from "./dates";
import type { MiContenidoPresetDays } from "./miContenidoScope";

export type ResolveMiContenidoDateRangeInput = {
  dateMode: MiProfileDateMode;
  presetDays: MiContenidoPresetDays;
  customStartDate: string | null;
  customEndDate: string | null;
  userRole?: string;
  company: MiContenidoDateFilterCompany | null | undefined;
  /** Optional user overrides for Mi contenido date policy (fallback to company). */
  user?: MiContenidoDateFilterUser | null | undefined;
  minBound: string;
  maxBound: string;
  searchParams?: URLSearchParams;
};

/**
 * Single source of truth for Mi contenido date range (Sidebar prefetch + ProfilePage query key).
 * Company/user policy min/max are applied as bounds via `minBound` / `maxBound`.
 */
export function resolveMiContenidoDateRange(
  input: ResolveMiContenidoDateRangeInput,
): { startDate: string; endDate: string } {
  const {
    dateMode,
    presetDays,
    customStartDate,
    customEndDate,
    minBound,
    maxBound,
    searchParams,
  } = input;

  let raw: { startDate: string; endDate: string };
  if (searchParams) {
    const fromUrl = parseYmdRangeFromSearchParams(searchParams);
    if (fromUrl) {
      raw = fromUrl;
    } else if (
      dateMode === "custom" &&
      customStartDate &&
      customEndDate &&
      isValidYmdRange(customStartDate, customEndDate)
    ) {
      raw = { startDate: customStartDate, endDate: customEndDate };
    } else {
      raw = dateRangeForPreset(presetDays);
    }
  } else if (
    dateMode === "custom" &&
    customStartDate &&
    customEndDate &&
    isValidYmdRange(customStartDate, customEndDate)
  ) {
    raw = { startDate: customStartDate, endDate: customEndDate };
  } else {
    raw = dateRangeForPreset(presetDays);
  }

  return clampYmdRangeToBounds(raw.startDate, raw.endDate, minBound, maxBound);
}
